import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputBase,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FitnessCenter as FitnessIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {getExercises, deleteExercise} from "../services/ExcersiceService";
import {useCompany} from "../contexts/CompanyContext";
import {useAuth} from "../contexts/AuthContext";
import {useMessageContext} from "../contexts/MessageContext";
import {FilterDropdown, StatusChip, Pagination} from "../componenets/ui";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {resistanceTypes} from "../enums/resistanceTypes";
import {targetMuscles} from "../enums/TargetMuscles";
import ExerciseDialog from "../componenets/exercises/ExerciseDialog";
import DeleteExerciseDialog from "../componenets/exercises/DeleteExerciseDialog";
import {canDeleteExercise} from "../utils/exercisePermissions";
import {imageUrl} from "../utils/imageUrl";

/* ── Constants & helpers ─────────────────────────────────── */
const PAGE_SIZE = 8;

const findTargetMuscle = (val) => targetMuscles.find(t => t.value === val) || null;
const findResistanceType = (val) => resistanceTypes.find(r => r.value === val) || null;

const RESISTANCE_OPTS = [{value: "ALL", label: "הכול"}, ...resistanceTypes.map(r => ({value: r.value, label: r.name}))];
const MUSCLE_OPTS = [{value: "ALL", label: "הכול"}, ...targetMuscles.map(m => ({value: m.value, label: m.name}))];

/* ── Table cell presets (match CoachDashboard / Clients) ─── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   Exercises page
   ═══════════════════════════════════════════════════════════ */
const Exercises = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {company} = useCompany();
  const {isSuperAdmin, isCompanyAdmin, isCompanyGroupAdmin} = useAuth();
  const isGroupAdmin = isCompanyGroupAdmin();
  const {notificationHandler} = useMessageContext();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [newExerciseOpen, setNewExerciseOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({open: false, exercise: null});
  const [isDeleting, setIsDeleting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [f, setF] = useState({
    exercises: [],
    total: 0,
    totalPages: 0,
    keyword: query.get("keyword") || "",
    resistanceType: query.get("resistanceType") || null,
    targetMuscle: query.get("targetMuscle") || null,
    scope: query.get("scope") || "ALL",
    page: +(query.get("page") || 0),
    loading: true,
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
  }, []);

  // URL sync
  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.resistanceType) p.set("resistanceType", ns.resistanceType);
    if (ns.targetMuscle) p.set("targetMuscle", ns.targetMuscle);
    if (ns.scope && ns.scope !== "ALL") p.set("scope", ns.scope);
    if (ns.page > 0) p.set("page", ns.page);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  };

  const set = (patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); };

  // Fetch exercises
  useEffect(() => {
    setF(p => ({...p, loading: true}));
    const rt = findResistanceType(f.resistanceType);
    const tm = findTargetMuscle(f.targetMuscle);
    getExercises(f.page, PAGE_SIZE, f.keyword, rt, tm, company?.id, undefined, false, f.scope)
        .then(r => setF(p => ({
          ...p,
          exercises: r.data.content,
          total: r.data.totalElements,
          totalPages: r.data.totalPages,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
  }, [f.page, f.keyword, f.resistanceType, f.targetMuscle, f.scope, company, refreshKey]);

  const reload = () => {
    setF(p => ({...p, keyword: "", resistanceType: null, targetMuscle: null, scope: "ALL", page: 0, loading: true}));
    setSearchInput("");
    navigate(location.pathname, {replace: true});
    setRefreshKey(k => k + 1);
  };

  const canDelete = (exercise) => canDeleteExercise(exercise, {
    isSuperAdmin: isSuperAdmin(),
    isCompanyAdmin: isCompanyAdmin(),
    companyId: company?.id,
  });

  const handleDelete = async () => {
    if (!deleteDialog.exercise?.id) return;
    setIsDeleting(true);
    try {
      await deleteExercise(deleteDialog.exercise.id);
      notificationHandler.success("התרגיל נמחק בהצלחה");
      setDeleteDialog({open: false, exercise: null});
      setRefreshKey(k => k + 1);
    } catch (error) {
      const errorMessage = error.response?.data || "שגיאה במחיקת התרגיל";
      notificationHandler.error(
          typeof errorMessage === "string" ? errorMessage : "שגיאה במחיקת התרגיל"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const pages = Math.ceil(f.total / PAGE_SIZE);

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
        {/* Dialogs */}
        <ExerciseDialog isOpen={newExerciseOpen} close={() => setNewExerciseOpen(false)} setReload={reload}/>
        <DeleteExerciseDialog
            open={deleteDialog.open}
            onClose={() => !isDeleting && setDeleteDialog({open: false, exercise: null})}
            onConfirm={handleDelete}
            exercise={deleteDialog.exercise}
            isLoading={isDeleting}
        />

        {/* ─── Title row (on canvas) ──────────────────────── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              תרגילים
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול תרגילים</Typography>
          </Box>
          <Box component="button" onClick={() => setNewExerciseOpen(true)} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff",
            height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            תרגיל חדש
          </Box>
        </Box>

        {/* ─── Table card ──────────────────────────────────── */}
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>

          {/* Filter bar inside card */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0, direction: "rtl",
          }}>
            {/* Count pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.greenText,
              background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <FitnessIcon sx={{fontSize: 14}}/>
              {f.total} תרגילים
            </Box>

            {/* Search */}
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש לפי שם תרגיל..."
                  sx={{
                    width: 200,
                    padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.white,
                    color: t.text,
                    fontSize: "12px",
                    "& input": {padding: 0},
                    "& input::placeholder": {color: t.t3, opacity: 1},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>

            {/* Filters */}
            <FilterDropdown label="סוג התנגדות" value={f.resistanceType || "ALL"}
                            onChange={v => set({resistanceType: v === "ALL" ? null : v})} options={RESISTANCE_OPTS} minWidth={130}/>
            <FilterDropdown label="קבוצת שרירים" value={f.targetMuscle || "ALL"}
                            onChange={v => set({targetMuscle: v === "ALL" ? null : v})} options={MUSCLE_OPTS} minWidth={140}/>

            {/* "My exercises only" toggle — Company Group Administrators only. Their library is the
                System Administrator's exercises plus their own; on = own only (scope OWN), off = all
                (scope ALL). Mirrors the "My templates only" toggle on the Workout Programs screen. */}
            {isGroupAdmin && (
                <Box
                    onClick={() => set({scope: f.scope === "OWN" ? "ALL" : "OWN"})}
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: "4px",
                      fontSize: "12px", fontWeight: 700, cursor: "pointer", userSelect: "none",
                      padding: "5px 12px", borderRadius: "20px", whiteSpace: "nowrap",
                      transition: "all .15s",
                      color: f.scope === "OWN" ? t.white : t.t3,
                      background: f.scope === "OWN" ? t.green : t.white,
                      border: `1.5px solid ${f.scope === "OWN" ? t.green : t.border}`,
                    }}
                >
                  התרגילים שלי בלבד
                </Box>
            )}
          </Box>

          {/* Table area */}
          {f.loading && !f.exercises.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !f.exercises.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין תרגילים</Typography>
                <Typography sx={{color: t.t3, fontSize: "13px"}}>נסה לשנות את הסינון</Typography>
              </Box>
          ) : (
              <>
                <TableContainer sx={{overflowY: "auto", maxHeight: 540, direction: "rtl"}}>
                  <Table stickyHeader sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{...th, textAlign: "right"}}>שם תרגיל</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "15%", display: {xs: "none", md: "table-cell"}}}>קבוצת שרירים</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "15%", display: {xs: "none", md: "table-cell"}}}>סוג התנגדות</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 100, display: {xs: "none", md: "table-cell"}}}>תמונה</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 72}}>פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.exercises.map(exercise => {
                        const tmName = targetMuscles.find(m => m.value === exercise.targetMuscle)?.name;
                        const rtName = resistanceTypes.find(r => r.value === exercise.resistanceType)?.name;
                        const showDelete = canDelete(exercise);
                        return (
                            <TableRow key={exercise.id} onClick={() => navigate(`/app/exercises/${exercise.id}`)}
                                      sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                              <TableCell sx={{...td, textAlign: "right"}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                  <Typography sx={{fontSize: "12.5px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    {exercise.nameHeb}
                                  </Typography>
                                  {exercise.variant && <StatusChip label="מותאם" variant="info" size="small"/>}
                                  {exercise.ownerUserId && <StatusChip label="מנהל קבוצת חברות" variant="warning" size="small"/>}
                                </Box>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                                <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>{tmName || "—"}</Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                                <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>{rtName || "—"}</Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                {(exercise.imageUrl || exercise.coverUrl) ? (
                                    <Box component="img" src={imageUrl(exercise.imageUrl || exercise.coverUrl, 56)}
                                         loading="lazy"
                                         sx={{width: 56, height: 56, borderRadius: t.rxs, objectFit: "cover"}}/>
                                ) : (
                                    <Typography sx={{fontSize: "12px", color: t.t3}}>—</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center"}} onClick={e => e.stopPropagation()}>
                                {showDelete ? (
                                    <Tooltip title="מחק תרגיל">
                                      <IconButton
                                          size="small"
                                          onClick={() => setDeleteDialog({open: true, exercise})}
                                          sx={{color: t.red, "&:hover": {background: t.redLight}}}
                                      >
                                        <DeleteIcon fontSize="small"/>
                                      </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Typography sx={{fontSize: "12px", color: t.t3}}>—</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                )}
              </>
          )}
        </Box>
      </Box>
  );
};

export default Exercises;
